import "./LocationPermission.scss";

import {
    IonButton,
    IonContent,
    IonLabel,
    IonModal,
    IonAlert
} from "@ionic/react";
import {useHistory} from "react-router";

import {RoutePath} from "../../../model/RoutePath.enum";
import {MainFooter, MainHeader} from "../../Layout/Layout";
import {Trans, useTranslation} from "react-i18next";
import {useStateMachine} from "little-state-machine";
import React, {useState} from "react";
import {useGeolocationPermission} from "../../../hooks/useGeolocationPermission";
import {updateDetectedPoint, updateRdvPoint} from "../../../state/updateState";

// Define browser troubleshooting entries
type BrowserEntry = {
    key: string;
    label: string;
    icon: string;
    instructions: JSX.Element;
};

const browsers: BrowserEntry[] = [
    {
        key: "chrome",
        label: "Chrome",
        icon: "/assets/images/chromeIcon.png",
        instructions: (
            <Trans
                i18nKey="locationPermission_chromeInstructions"
                components={{
                    ol: <ol />,
                    li: <li />,
                }}
            />
        ),
    },
    {
        key: "firefox",
        label: "Firefox",
        icon: "/assets/images/firefoxIcon.png",
        instructions: (
            <Trans
                i18nKey="locationPermission_firefoxInstructions"
                components={{
                    ol: <ol />,
                    li: <li />,
                }}
            />
        ),
    },
    {
        key: "safari",
        label: "Safari",
        icon: "/assets/images/safariIcon.png",
        instructions: (
            <Trans
                i18nKey="locationPermission_safariInstructions"
                components={{
                    p: <p />,
                }}
            />
        ),
    },
];

const ACCURACY_THRESHOLD = 20; // meters
const LocationPermission: React.FC = () => {
    const history = useHistory();
    const {actions, state} = useStateMachine({updateRdvPoint, updateDetectedPoint});
    const {t} = useTranslation();
    const [showDeniedAlert, setShowDeniedAlert] = useState(false);

    const handleOnNext = () => {
        history.push(`${RoutePath.REQUEST}/${RoutePath.CONFIRM_RDV_POINT}`);
    };
    const handleContinueWithoutGPS = () => {
        history.push(`${RoutePath.REQUEST}/${RoutePath.MANUAL_LOCATION}`);
    };

    const locationPermission = useGeolocationPermission();

    const [showTroubleshoot, setShowTroubleshoot] = useState(false);
    const [selectedBrowser, setSelectedBrowser] = useState<BrowserEntry | null>(null);

    const openTroubleshoot = () => {
        setShowTroubleshoot(true);
    };

    const closeTroubleshoot = () => {
        setShowTroubleshoot(false);
        setSelectedBrowser(null);
    };
    const isOnHighway = state.summary.immobilizedOnHighway;

    const handleLocateMeNowClick = () => {
        if (!navigator.geolocation) {
            alert(t("locationPermission_GPSTurnedOff"));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const {latitude, longitude} = position.coords;
                actions.updateRdvPoint({lat: latitude, lng: longitude});
                handleOnNext();
            },
            (error) => {
                setShowDeniedAlert(true);
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        // User denied permission
                        console.warn("User denied geolocation permission.");
                        break;
                    case error.POSITION_UNAVAILABLE:
                        console.warn("Location unavailable.");
                        // showToast("Location data unavailable. Please try again later.");
                        break;

                    case error.TIMEOUT:
                        console.warn("Geolocation request timed out.");
                        // showToast("Request timed out. Try again or tap location manually.");
                        break;

                    default:
                        console.warn("Unknown geolocation error.");
                        // showToast("An unexpected error occurred while retrieving your location.");
                        break;
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
            });
    };

    const baseImagePath = `${process.env.PUBLIC_URL}/assets/images/${process.env.REACT_APP_ALT_ICONS_PATH || ""}`;
    const imagePath = isOnHighway
        ? `${baseImagePath}highway.svg`
        : `${baseImagePath}Home.svg`;

    return (
        <IonContent className={"locationPermission_content"}>
            <MainHeader
                title={t("locationPermission_provideLocation")}
                image={imagePath}
            >
                {locationPermission === "denied" && (
                    <>
                        <p>{t("locationPermission_GPSTurnedOff")}</p>
                    </>
                )}
                {locationPermission !== "denied" && (
                    <>
                        <p>{t("locationPermission_pressLocateMeNow")}</p>
                    </>
                )}
                <p>
                    <Trans
                        i18nKey="locationPermission_guidance"
                        values={{
                            chPhone: process.env.REACT_APP_PHONE_NUMBER,
                            etiPhone: process.env.REACT_APP_PHONE_NUMBER_ABROAD
                        }}
                        components={{
                            guide: (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        openTroubleshoot();
                                    }}
                                />
                            ),
                            ch: (
                                <a href={`tel:${process.env.REACT_APP_PHONE_NUMBER}`}/>
                            ),
                            eti: (
                                <a href={`tel:${process.env.REACT_APP_PHONE_NUMBER_ABROAD}`}/>
                            )
                        }}
                    />
                </p>
                <IonButton
                    id="locationPermission_locateMeNow_btn"
                    className="button-tcs cta"
                    color="primary"
                    expand="block"
                    onClick={handleLocateMeNowClick}
                >
                    <IonLabel>{t("locationPermission_locateMeNow")}</IonLabel>
                </IonButton>
                {!isOnHighway && (
                    <>
                        <p>{t("locationPermission_proceedWithoutGPS")}</p>
                        <IonButton
                            id="locationPermission_proceedWithoutGPS_btn"
                            className="button-tcs cta"
                            color="primary"
                            expand="block"
                            onClick={handleContinueWithoutGPS}
                        >
                            <IonLabel>{t("locationPermission_proceedWithoutGPS_btn")}</IonLabel>
                        </IonButton>
                    </>
                )}
                <IonModal isOpen={showTroubleshoot} onDidDismiss={closeTroubleshoot}>
                    <h2 className="browserSelectionTitle">{t("locationPermission_browserSelectionHeader")}</h2>
                    <div className="browserSelection">
                        {!selectedBrowser && (
                            <div className="browserIcons">
                                {browsers.map((browser) => (
                                    <button
                                        key={browser.key}
                                        onClick={() => setSelectedBrowser(browser)}
                                        className="browserButton"
                                    >
                                        <img src={browser.icon} alt={browser.label}/>
                                        <div>{browser.label}</div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {selectedBrowser && (
                            <div className="browserModalContent">
                                <img src={selectedBrowser.icon} alt={selectedBrowser.label}/>
                                <h2>{selectedBrowser.label}</h2>
                                <div>{selectedBrowser.instructions}</div>
                                <IonButton expand="block" onClick={() => setSelectedBrowser(null)}>
                                    {t("Back")}
                                </IonButton>
                            </div>
                        )}
                        <IonButton expand="block" color="medium" onClick={closeTroubleshoot}>
                            {t("Close")}
                        </IonButton>
                    </div>
                </IonModal>
                <IonAlert
                    isOpen={showDeniedAlert}
                    onDidDismiss={() => setShowDeniedAlert(false)}
                    header={t("locationPermission_locationNotAvailable")}
                    message={t("locationPermission_highwayMandatoryGPS")}
                    buttons={[{text: t("OK"), role: "cancel", cssClass: "button-tcs customAlertYellowButton",}]}
                />
            </MainHeader>
        </IonContent>
    );
};

export default LocationPermission;
